import { Component, Input, Output, EventEmitter, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';

import { BaseChartDirective } from 'ng2-charts';
import { MOCK_NET_WORTH_TREND_DATA, NetWorthTrendData } from './net-worth-trend-modal.mock';
import {
    Chart as ChartJS,
    ChartConfiguration,
    ChartType,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    LineController,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { ModalComponent } from '../../../../../shared/components/modal/modal';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    LineController,
    Title,
    Tooltip,
    Legend,
    Filler
);

@Component({
    selector: 'app-net-worth-trend-modal',
    standalone: true,
    imports: [CommonModule, ModalComponent, BaseChartDirective],
    templateUrl: './net-worth-trend-modal.html',
    styleUrls: ['./net-worth-trend-modal.scss']
})
export class NetWorthTrendModalComponent implements OnInit {
    @Input() isOpen: boolean = false;
    @Output() close = new EventEmitter<void>();

    @ViewChild(BaseChartDirective) chart: BaseChartDirective | undefined;

    // Data from "API"
    apiData: NetWorthTrendData = MOCK_NET_WORTH_TREND_DATA;
    activeTab: 'daily' | 'monthly' | 'weekly' | 'annually' = 'monthly';

    public lineChartData: ChartConfiguration['data'] = {
        datasets: [
            {
                data: [],
                borderColor: '#26BC8F',
                backgroundColor: 'rgba(38, 188, 143, 0.1)',
                borderWidth: 2,
                tension: 0.4,
                pointRadius: 0,
                pointHoverRadius: 4,
                fill: true
            }
        ],
        labels: []
    };

    public lineChartOptions: ChartConfiguration['options'] = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                enabled: true,
                intersect: false,
                mode: 'index',
                callbacks: {
                    label: (context) => {
                        const value = context.parsed.y;
                        return value !== null ? `₹${value.toLocaleString()}` : '';
                    }
                }
            }
        },
        scales: {
            x: {
                display: true,
                grid: {
                    display: false
                },
                border: {
                    display: false
                },
                ticks: {
                    color: '#8E8E93',
                    font: {
                        size: 12
                    },
                    autoSkip: false,
                    maxRotation: 0,
                    padding: 10
                }
            },
            y: {
                display: true,
                position: 'right',
                grid: {
                    color: () => getComputedStyle(document.body).getPropertyValue('--chart-grid-color') || 'rgba(0, 0, 0, 0.05)',
                    drawTicks: false
                },
                border: {
                    display: false
                },
                ticks: {
                    color: '#8E8E93',
                    font: {
                        size: 12
                    },
                    padding: 10,
                    callback: (value) => {
                        if (Number(value) >= 1000) {
                            return (Number(value) / 1000) + 'k';
                        }
                        return value;
                    }
                }
            }
        },
        elements: {
            line: {
                borderWidth: 2
            }
        }
    };

    public lineChartType: ChartType = 'line';

    ngOnInit() {
        this.updateChartData();
    }

    updateChartData() {
        const currentTrend = this.apiData.trends[this.activeTab];
        this.lineChartData.datasets[0].data = currentTrend.values;

        // Generate labels for the chart
        // For daily, we only show specific labels at intervals
        if (this.activeTab === 'daily') {
            const labels = new Array(currentTrend.values.length).fill('');
            const displayLabels = currentTrend.displayLabels;

            // Distribute display labels evenly
            labels[Math.floor(labels.length * 0.1)] = displayLabels[0];
            labels[Math.floor(labels.length * 0.35)] = displayLabels[1];
            labels[Math.floor(labels.length * 0.6)] = displayLabels[2];
            labels[Math.floor(labels.length * 0.85)] = displayLabels[3];

            this.lineChartData.labels = labels;
        } else {
            this.lineChartData.labels = currentTrend.displayLabels;
        }

        // Update Y axis max based on data
        const maxVal = Math.max(...currentTrend.values);
        if (this.lineChartOptions?.scales?.['y']) {
            this.lineChartOptions.scales['y'].max = Math.ceil(maxVal * 1.2 / 10) * 10;
        }

        // Force chart update
        this.chart?.update();
    }

    onTabChange(tab: 'daily' | 'monthly' | 'weekly' | 'annually') {
        this.activeTab = tab;
        this.updateChartData();
    }

    onDone() {
        this.close.emit();
    }

    onCancel() {
        this.close.emit();
    }
    get isPositive() {
        return this.apiData.fundInfo.trend.startsWith('+');
    }
}

